import type { FxModule } from "pixelfx";
import * as blinkIn from "pixelfx/fx/arcane/blink-in";
import * as blinkOut from "pixelfx/fx/arcane/blink-out";
import * as bluntHit from "pixelfx/fx/impacts/blunt-hit";
import * as critHit from "pixelfx/fx/impacts/crit-hit";
import * as heavyLanding from "pixelfx/fx/impacts/heavy-landing";
import * as shieldBlock from "pixelfx/fx/impacts/shield-block";
import * as poof from "pixelfx/fx/spawn/poof";
import * as bloodBurst from "./effects/blood-burst";
import * as dustLanding from "./effects/dust-landing";
import * as hdSpark from "./effects/hd-spark";
import * as cloud from "./effects/particle-cloud";
import * as smokePuff from "./effects/smoke-puff";

export interface FxEntry {
  readonly module: FxModule;
  readonly options?: Readonly<Record<string, unknown>>;
}

/** Холодная искра блока: тот же hit-spark в палитре стали — мельче и тише попадания. */
const BLOCK_PALETTE = ["#0e1a2a", "#1f3a5a", "#3f6f9a", "#7fb0d8", "#c8e6ff", "#ffffff"];
/** Телепорт и рывок сквозь пространство: та же искра в фиолетовой гамме, без белой фигуры на пол-экрана. */
const BLINK_PALETTE = ["#1a0a2a", "#3a1a5a", "#6a3aa0", "#a070e0", "#d8b8ff", "#f4ecff"];

const plain = (entries: readonly (readonly [string, FxModule])[]): [string, FxEntry][] =>
  entries.map(([id, module]) => [id, { module }]);

/** Эффекты, которые зовут способности по id. Новый id в данных способности — новая строка здесь. */
export const FX_LIBRARY: ReadonlyMap<string, FxEntry> = new Map<string, FxEntry>([
  // Искры в нашем масштабе (боец 190 px арта): лёгкая, тяжёлая, блок, телепорт
  ["impacts/hit-spark", { module: hdSpark, options: { strength: 0 } }],
  ["heavy/spark", { module: hdSpark, options: { strength: 1 } }],
  // частицы суперприёма: облако к кулаку, вихрь на попадании, взрыв на финале
  ["particles/gather", { module: cloud, options: { mode: "gather", count: 280, radius: 150 } }],
  // заряд удержанием: частицы стягиваются к кулаку или стопе и растут по ступеням, полный заряд — ярче и шире
  ["charge/1", { module: cloud, options: { mode: "gather", count: 180, radius: 110, color: "#ffd24a" } }],
  ["charge/2", { module: cloud, options: { mode: "gather", count: 300, radius: 150, color: "#ffb040" } }],
  [
    "charge/3",
    { module: cloud, options: { mode: "gather", count: 300, radius: 140, color: "#ff8a3a", hot: "#ffffff" } },
  ],
  // телепорт (Джон 04.11.2026: «без эффектов магии и слишком резкая»): рассыпаться, дуга, собраться
  [
    "teleport/dissolve",
    { module: cloud, options: { mode: "burst", count: 600, radius: 120, color: "#a070e0", hot: "#f4ecff" } },
  ],
  [
    "teleport/trail",
    { module: cloud, options: { mode: "burst", count: 110, radius: 36, color: "#b890ff", hot: "#f4ecff" } },
  ],
  // уход и появление: события способности — их раскладывает на части FxSystem (след, дуга, волна)
  [
    "teleport/out",
    { module: cloud, options: { mode: "burst", count: 360, radius: 90, color: "#a070e0", hot: "#f4ecff" } },
  ],
  ["teleport/in", { module: cloud, options: { mode: "gather", count: 320, radius: 120, color: "#a070e0" } }],
  ["teleport/gather", { module: cloud, options: { mode: "gather", count: 320, radius: 120, color: "#a070e0" } }],
  // аура Ярости при полной шкале — огненные искры со всех сторон стягиваются к телу, мягко, без вспышек
  [
    "aura/rage",
    { module: cloud, options: { mode: "gather", count: 70, radius: 90, color: "#ff5a2a", hot: "#ffb040" } },
  ],
  ["particles/vortex", { module: cloud, options: { mode: "vortex", count: 220, radius: 90, color: "#ff8a3a" } }],
  [
    "particles/burst",
    { module: cloud, options: { mode: "burst", count: 420, radius: 160, color: "#ffb040", hot: "#ffffff" } },
  ],
  // «Взлёт»: огонь от кулака на старте прыжка с апперкотом
  [
    "fire/rise",
    { module: cloud, options: { mode: "burst", count: 260, radius: 90, color: "#ff5a10", hot: "#ffe0a0" } },
  ],
  // жест узнан, а Ярости нет: тусклый дымок у груди — «не хватает силы»
  [
    "rage/empty",
    { module: cloud, options: { mode: "burst", count: 90, radius: 46, color: "#6a6070", hot: "#b0a8b8" } },
  ],
  // магический выстрел: вспышка у рук на выстреле, взрыв сгустка на попадании и в блоке, встреча двух снарядов, гаснет у стены
  [
    "magic/cast",
    { module: cloud, options: { mode: "burst", count: 220, radius: 70, color: "#8a5aff", hot: "#e8f0ff" } },
  ],
  [
    "magic/hit",
    { module: cloud, options: { mode: "burst", count: 480, radius: 160, color: "#7a6aff", hot: "#f0f6ff" } },
  ],
  [
    "magic/block",
    { module: cloud, options: { mode: "burst", count: 220, radius: 90, color: "#6a8aff", hot: "#e0eaff" } },
  ],
  [
    "magic/clash",
    { module: cloud, options: { mode: "burst", count: 620, radius: 200, color: "#b06aff", hot: "#ffffff" } },
  ],
  [
    "magic/fizzle",
    { module: cloud, options: { mode: "burst", count: 140, radius: 60, color: "#6a5aa0", hot: "#c8c0ff" } },
  ],
  // финалы веток комбо (Джон 04.11.2026: «разные крутые комбо-приёмы»): взрыв частиц своего цвета из точки удара
  [
    "finale/fire",
    { module: cloud, options: { mode: "burst", count: 520, radius: 170, color: "#ff6a20", hot: "#fff0c0" } },
  ],
  [
    "finale/storm",
    { module: cloud, options: { mode: "burst", count: 520, radius: 170, color: "#4aa8ff", hot: "#e8f6ff" } },
  ],
  [
    "finale/gold",
    { module: cloud, options: { mode: "burst", count: 520, radius: 170, color: "#ffd040", hot: "#ffffff" } },
  ],
  [
    "finale/void",
    { module: cloud, options: { mode: "burst", count: 520, radius: 170, color: "#a05aff", hot: "#f0e4ff" } },
  ],
  ["block/spark", { module: hdSpark, options: { strength: 0, palette: BLOCK_PALETTE } }],
  ["blink/spark", { module: hdSpark, options: { strength: 1, palette: BLINK_PALETTE } }],
  ...plain([
    ["arcane/blink-in", blinkIn],
    ["arcane/blink-out", blinkOut],
    ["impacts/blunt-hit", bluntHit],
    ["impacts/crit-hit", critHit],
    ["impacts/heavy-landing", heavyLanding],
    ["impacts/shield-block", shieldBlock],
    ["spawn/poof", poof],
    ["blood/burst", bloodBurst],
    ["dust/landing", dustLanding],
    ["smoke/puff", smokePuff],
  ]),
]);
